import { useQuery } from '@apollo/client/react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import { useState } from 'react';
import { Transaction } from '../models/transaction/';
import { GET_TRANSACTIONS } from '../queries/transactions';
import ModalNAMContractCreationEdition from '../components/modals/ModalNAMContractCreation';
import PayMeButton from '../components/buttons/PayMeButton';
import { getStatusColor } from '../utils/helper';

interface TransactionsData {
  transactions: {
    total_payment_received: number;
    total_quantity_delivered: number;
    transactions_list: Transaction[];
  };
  balance: {
    total_due: number;
    balance_signature: string;
    can_request_balance: boolean;
  };
}

const TransactionsPage = () => {
  const [startAt, setStartAt] = useState<Dayjs | null>(dayjs().subtract(30, 'day'));
  const [endAt, setEndAt] = useState<Dayjs | null>(dayjs());
  const [offset] = useState(0);
  const [limit] = useState(100);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedStatementId, setSelectedStatementId] = useState<number | undefined>();

  const handleModalDismiss = (data?: any, role?: string) => {
    setIsModalOpen(false);
    setSelectedStatementId(undefined);
    if (role === 'confirm' && data) {
      console.log('Contract created with data:', data);
      refetch()
      // TODO: Refresh transactions list after creation
    }
  };

  const handleTransactionClick = (transaction: Transaction) => {
    if (transaction.isContract() && transaction.statement_id) {
      setSelectedStatementId(transaction.statement_id);
      setIsModalOpen(true);
    }
  };

  const { loading, error, data, refetch } = useQuery<TransactionsData>(GET_TRANSACTIONS, {
    variables: {
      offset,
      limit,
      startAt: startAt?.format('YYYY-MM-DD'),
      endAt: endAt?.format('YYYY-MM-DD'),
    },
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Error loading transactions: {error.message}</Alert>
      </Box>
    );
  }

  const transactions = (data?.transactions?.transactions_list || []).map((t) => new Transaction(t));
  const balance = data?.balance;
  const totalPaymentReceived = data?.transactions?.total_payment_received || 0;
  const totalQuantityDelivered = data?.transactions?.total_quantity_delivered || 0;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Transactions</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
            disabled={loading}
          >
            Reload
          </Button>
        </Box>
      </Box>

      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <DatePicker
            label="Start Date"
            value={startAt}
            onChange={(newValue) => setStartAt(newValue)}
          />
          <DatePicker
            label="End Date"
            value={endAt}
            onChange={(newValue) => setEndAt(newValue)}
          />
        </Box>
      </LocalizationProvider>

      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Typography variant="h6">
                Balance
              </Typography>
              <PayMeButton
                balanceSignature={balance?.balance_signature || ''}
                totalDue={balance?.total_due || 0}
                canRequestBalance={balance?.can_request_balance || false}
                onSuccess={() => refetch()}
              />
            </Box>
            <Typography variant="h4">{balance?.total_due?.toLocaleString() ?? 0}</Typography>
            <Typography variant="caption" color="text.secondary">
              Can request: {balance?.can_request_balance ? 'Yes' : 'No'}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Total Payment Received
            </Typography>
            <Typography variant="h4">{totalPaymentReceived.toLocaleString()}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Total Quantity Delivered
            </Typography>
            <Typography variant="h4">{totalQuantityDelivered.toLocaleString()} kg</Typography>
          </CardContent>
        </Card>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Party</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Quantity (kg)</TableCell>
              <TableCell align="right">Price/kg</TableCell>
              <TableCell align="right">Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transactions.map((transaction) => (
              <TableRow 
                key={transaction.id}
                onClick={() => handleTransactionClick(transaction)}
                sx={{
                  cursor: transaction.isContract() ? 'pointer' : 'default',
                  '&:hover': transaction.isContract() ? {
                    backgroundColor: 'action.hover'
                  } : {}
                }}
              >
                <TableCell>{transaction.id}</TableCell>
                <TableCell>
                  {dayjs(transaction.transaction_date).format('DD/MM/YYYY')}
                </TableCell>
                <TableCell>
                  {transaction.party}
                  {transaction.debtor && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      {transaction.debtor.firstname || transaction.debtor.lastname 
                        ? `${transaction.debtor.firstname || ''} ${transaction.debtor.lastname || ''}`.trim()
                        : transaction.debtor.organisation_name || '-'}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{transaction.title}</TableCell>
                <TableCell>
                  <Chip 
                    label={transaction.isPayment() ? 'Payment' : 'Contract'} 
                    size="small"
                    color={transaction.isPayment() ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell>
                  <Chip 
                    label={transaction.status} 
                    size="small"
                    sx={{
                      backgroundColor: getStatusColor(transaction.status),
                      color: '#FFFFFF',
                    }}
                  />
                </TableCell>
                <TableCell align="right">
                  {transaction.financials.quantity?.toLocaleString() ?? '-'}
                  {transaction.financials.estimated && ' (est)'}
                </TableCell>
                <TableCell align="right">
                  {transaction.financials.price_per_kg?.toLocaleString() ?? '-'}
                </TableCell>
                <TableCell align="right">
                  <Typography 
                    sx={{ 
                      color: transaction.isPayment()
                        ? (transaction.financials.flow !== 'CREDIT' ? 'error.main' : 'success.main')
                        : 'text.primary',
                      fontWeight: 'bold'
                    }}
                  >
                    {transaction.isPayment() && (transaction.financials.flow !== 'CREDIT' ? '-' : '+')}
                    {transaction.financials.total?.toLocaleString() ?? '-'}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {transactions.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary">No transactions found</Typography>
        </Box>
      )}

      <ModalNAMContractCreationEdition
        statementId={selectedStatementId}
        isOpen={isModalOpen}
        onDismiss={handleModalDismiss}
      />
    </Box>
  );
};

export default TransactionsPage;
